import { useEffect, useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Checkbox,
  Container,
  FormControlLabel,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { LoadingButton } from "@mui/lab";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import GlobalHeader from "../../../components/GlobalHeader";
import SnackAlert from "../../../components/SnackAlert";
import useApi from "../../../hooks/useApi";
import useSnack from "../../../hooks/useSnack";
import { Link as RouterLink } from "react-router-dom";

export default function ProductorDirectLink() {
  const { loadApi, loadingApi } = useApi();
  const { msgSB, openSB, showSnack, typeSB } = useSnack();
  const [data, setData] = useState(null);
  const [bannerWidth, setBannerWidth] = useState(728);
  const [bannerHeight, setBannerHeight] = useState(90);
  const [bannerWidthInput, setBannerWidthInput] = useState("728");
  const [bannerHeightInput, setBannerHeightInput] = useState("90");
  const apiBase = (
    process.env.REACT_APP_API_URL || "https://api.freim.tv/api"
  ).replace(/\/$/, "");

  const directUrl = useMemo(() => {
    if (!data?.direct_slug) return "";
    return `${window.location.origin}/d/${data.direct_slug}`;
  }, [data?.direct_slug]);

  const qrUrl = data?.direct_slug
    ? `${apiBase}/general/direct-links/${data.direct_slug}/qr.png`
    : "";

  const bannerUrl = (size) =>
    data?.direct_slug
      ? `${apiBase}/general/direct-links/${data.direct_slug}/banner/${size}.svg`
      : "";

  const BANNER_VERSION = "20261003-3";

  const customBannerUrl = data?.direct_slug
    ? `${apiBase}/general/direct-links/${data.direct_slug}/banner/custom.svg?width=${bannerWidth}&height=${bannerHeight}&v=${BANNER_VERSION}`
    : "";

  const embedCode = (src, width, height) =>
    `<a href="${directUrl}" target="_blank" rel="noopener noreferrer"><img src="${src}" width="${width}" height="${height}" alt="Envía tu música a ${
      data?.nombre_media || "este medio"
    } vía RadarFreim"></a>`;

  const presets = [
    { label: "728 × 90", width: 728, height: 90 },
    { label: "300 × 250", width: 300, height: 250 },
    { label: "300 × 600", width: 300, height: 600 },
  ];

  const soloDigitos = (value) => String(value || "").replace(/\D/g, "");

  const confirmarDimension = (tipo) => {
    const esAncho = tipo === "width";
    const raw = esAncho ? bannerWidthInput : bannerHeightInput;
    const actual = esAncho ? bannerWidth : bannerHeight;
    const min = esAncho ? 200 : 60;
    const max = 1600;
    const parsed = Number.parseInt(raw, 10);

    if (!Number.isFinite(parsed)) {
      if (esAncho) setBannerWidthInput(String(actual));
      else setBannerHeightInput(String(actual));
      return;
    }

    const next = Math.max(min, Math.min(max, parsed));

    if (esAncho) {
      setBannerWidth(next);
      setBannerWidthInput(String(next));
    } else {
      setBannerHeight(next);
      setBannerHeightInput(String(next));
    }
  };

  const usarPreset = (width, height) => {
    setBannerWidth(width);
    setBannerHeight(height);
    setBannerWidthInput(String(width));
    setBannerHeightInput(String(height));
  };

  useEffect(() => {
    loadApi("productor/direct-links", true, "get")
      .then(({ data }) => setData(data))
      .catch((e) => showSnack(e.message, "error"));
  }, []);

  const updateService = (id, patch) => {
    setData((prev) => ({
      ...prev,
      services: prev.services.map((service) =>
        service.id === id ? { ...service, ...patch } : service
      ),
    }));
  };

  const guardar = async () => {
    const services = data.services.map((service) => ({
        id: service.id,
        activo: Boolean(service.activo),
        titulo: service.nombre || "",
        descripcion: service.descripcion,
      }));

    try {
      const { data: response } = await loadApi(
        "productor/direct-links",
        true,
        "put",
        { services }
      );
      showSnack(response.message);
      const fresh = await loadApi("productor/direct-links", true, "get");
      setData(fresh.data);
    } catch (e) {
      showSnack(e.message, "error");
    }
  };

  const copiarTexto = async (texto, mensaje) => {
    try {
      await navigator.clipboard.writeText(texto);
      showSnack(mensaje);
    } catch {
      showSnack("No se pudo copiar al portapapeles.", "error");
    }
  };

  const copiar = () => copiarTexto(directUrl, "Direct Link copiado.");

  return (
    <Stack bgcolor="#f0f1f7" minHeight="100vh" pb={6}>
      <GlobalHeader />
      <Container maxWidth="md">
        <Stack spacing={3}>
          <Box>
            <Typography variant="h4" color="primary.main" fontWeight={900}>
              MI DIRECT LINK
            </Typography>
            <Typography color="text.secondary">
              Compártelo con artistas que te escriben por correo o redes para que
              puedan contratar una acción de difusión directamente contigo.
            </Typography>
          </Box>

          {data && !data.verificado && (
            <Alert severity="info">
              Puedes configurarlo desde ahora, pero el enlace público se habilita
              cuando tu medio esté verificado.
            </Alert>
          )}

          {/* Los servicios de Direct Links cuestan 5 RC: el enlace se
              publica cuando el equipo activa los envíos de 5 RC. */}
          {data && data.verificado && !data.activo5RC && (
            <Alert
              severity="info"
              action={
                <Button component={RouterLink} to="/productor/perfil" size="small">
                  Solicitarlo
                </Button>
              }
            >
              Tu enlace público se habilita cuando el equipo active tus envíos
              de 5 RC. Solicítalo desde tu perfil registrando el canal donde
              publicas.
            </Alert>
          )}

          {data && (
            <>
              <Card sx={{ borderRadius: 3 }}>
                <CardContent>
                  <Typography fontWeight={800}>Tu enlace público</Typography>
                  <Stack direction={{ xs: "column", sm: "row" }} gap={1} mt={1}>
                    <TextField value={directUrl} fullWidth InputProps={{ readOnly: true }} />
                    <Button startIcon={<ContentCopyIcon />} onClick={copiar}>
                      Copiar
                    </Button>
                  </Stack>
                </CardContent>
              </Card>

              {data.verificado && data.activo5RC && (
                <Card sx={{ borderRadius: 3 }}>
                  <CardContent>
                    <Typography variant="h6" fontWeight={900}>
                      QR y banners para compartir
                    </Typography>
                    <Typography color="text.secondary" mb={3}>
                      Úsalos en tu web, media kit, correo, Linktree o redes. Todos
                      apuntan a tu Direct Link y se actualizan sin que tengas que
                      volver a subir imágenes.
                    </Typography>

                    <Stack
                      direction={{ xs: "column", md: "row" }}
                      spacing={3}
                      alignItems={{ xs: "stretch", md: "flex-start" }}
                    >
                      <Stack spacing={1.5} alignItems="center">
                        <Typography fontWeight={800}>Código QR</Typography>
                        <Box
                          component="img"
                          src={qrUrl}
                          alt="QR de tu Direct Link"
                          sx={{
                            width: 180,
                            height: 180,
                            bgcolor: "white",
                            borderRadius: 2,
                            p: 1,
                            border: "1px solid",
                            borderColor: "divider",
                          }}
                        />
                        <Button
                          variant="outlined"
                          onClick={() =>
                            copiarTexto(directUrl, "Enlace del QR copiado.")
                          }
                        >
                          Copiar enlace
                        </Button>
                      </Stack>

                      <Stack spacing={2} flex={1} minWidth={0}>
                        <Box>
                          <Typography fontWeight={900} mb={0.5}>
                            Generador de banner
                          </Typography>
                          <Typography variant="body2" color="text.secondary" mb={2}>
                            Elige una medida rápida o escribe el ancho y alto que
                            mejor se adapten a tu sitio. El diseño y los textos de
                            RadarFreim se mantienen automáticamente.
                          </Typography>

                          <Stack
                            direction="row"
                            gap={1}
                            flexWrap="wrap"
                            mb={2}
                          >
                            {presets.map((preset) => (
                              <Button
                                key={preset.label}
                                size="small"
                                variant={
                                  bannerWidth === preset.width &&
                                  bannerHeight === preset.height
                                    ? "contained"
                                    : "outlined"
                                }
                                onClick={() =>
                                  usarPreset(preset.width, preset.height)
                                }
                              >
                                {preset.label}
                              </Button>
                            ))}
                          </Stack>

                          <Stack
                            direction={{ xs: "column", sm: "row" }}
                            spacing={1.5}
                            mb={2}
                          >
                            <TextField
                              label="Ancho (px)"
                              type="text"
                              value={bannerWidthInput}
                              inputProps={{ inputMode: "numeric" }}
                              onChange={(e) =>
                                setBannerWidthInput(soloDigitos(e.target.value))
                              }
                              onBlur={() => confirmarDimension("width")}
                              onKeyDown={(e) => {
                                if (e.key === "Enter") {
                                  confirmarDimension("width");
                                  e.currentTarget.blur();
                                }
                              }}
                              fullWidth
                            />
                            <TextField
                              label="Alto (px)"
                              type="text"
                              value={bannerHeightInput}
                              inputProps={{ inputMode: "numeric" }}
                              onChange={(e) =>
                                setBannerHeightInput(soloDigitos(e.target.value))
                              }
                              onBlur={() => confirmarDimension("height")}
                              onKeyDown={(e) => {
                                if (e.key === "Enter") {
                                  confirmarDimension("height");
                                  e.currentTarget.blur();
                                }
                              }}
                              fullWidth
                            />
                          </Stack>

                          <Box
                            sx={{
                              p: 2,
                              bgcolor: "#e7e8ee",
                              borderRadius: 2,
                              overflow: "auto",
                              maxHeight: 680,
                            }}
                          >
                            <Box
                              component="img"
                              src={customBannerUrl}
                              alt="Vista previa de banner de Direct Link"
                              sx={{
                                width: bannerWidth,
                                height: bannerHeight,
                                maxWidth: "none",
                                display: "block",
                              }}
                            />
                          </Box>

                          <TextField
                            value={embedCode(
                              customBannerUrl,
                              bannerWidth,
                              bannerHeight
                            )}
                            multiline
                            minRows={3}
                            fullWidth
                            InputProps={{ readOnly: true }}
                            sx={{ mt: 2 }}
                          />

                          <Button
                            sx={{ mt: 1 }}
                            startIcon={<ContentCopyIcon />}
                            onClick={() =>
                              copiarTexto(
                                embedCode(
                                  customBannerUrl,
                                  bannerWidth,
                                  bannerHeight
                                ),
                                `Código ${bannerWidth} × ${bannerHeight} copiado.`
                              )
                            }
                          >
                            Copiar código
                          </Button>
                        </Box>
                      </Stack>
                    </Stack>
                  </CardContent>
                </Card>
              )}

              <Card sx={{ borderRadius: 3 }}>
                <CardContent>
                  <Typography variant="h6" fontWeight={900}>
                    Servicios disponibles
                  </Typography>
                  <Typography color="text.secondary" mb={3}>
                    Todos los servicios de Direct Links cuestan 5 RadarCoins
                    (USD $12.50). Activa solamente los que realmente ofreces.
                  </Typography>

                  <Stack spacing={3}>
                    {data.services.map((service) => (
                      <Box
                        key={service.id}
                        sx={{
                          border: "1px solid",
                          borderColor: "divider",
                          borderRadius: 2,
                          p: 2,
                        }}
                      >
                        <FormControlLabel
                          control={
                            <Checkbox
                              checked={Boolean(service.activo)}
                              onChange={(e) =>
                                updateService(service.id, {
                                  activo: e.target.checked,
                                })
                              }
                            />
                          }
                          label={
                            <Typography fontWeight={800}>
                              {service.nombre}
                            </Typography>
                          }
                        />
                        <Typography color="text.secondary" mb={2}>
                          {service.tipoContenido === "evento"
                            ? "Este servicio pedirá la información de un evento."
                            : "Este servicio pedirá la información de un lanzamiento musical."}
                        </Typography>

                        {service.activo && (
                          <Stack spacing={2}>
                            <TextField
                              label="Nombre que verá el artista"
                              value={service.nombre}
                              onChange={(e) =>
                                updateService(service.id, {
                                  nombre: e.target.value,
                                })
                              }
                              fullWidth
                            />
                            <TextField
                              label="Descripción"
                              value={service.descripcion || ""}
                              onChange={(e) =>
                                updateService(service.id, {
                                  descripcion: e.target.value,
                                })
                              }
                              multiline
                              minRows={2}
                              fullWidth
                            />
                          </Stack>
                        )}
                      </Box>
                    ))}
                  </Stack>

                  <LoadingButton
                    sx={{ mt: 3 }}
                    loading={loadingApi.includes("put__productor/direct-links")}
                    onClick={guardar}
                  >
                    Guardar configuración
                  </LoadingButton>
                </CardContent>
              </Card>
            </>
          )}
        </Stack>
      </Container>
      <SnackAlert openSB={openSB} typeSB={typeSB} msgSB={msgSB} />
    </Stack>
  );
}
